import { useState, useEffect } from 'react';
import { DEFAULT_TARGET_SCORE, TABLE_TIERS, SHINY_EVENT, type GameMode, type GameVariant, type TableTier } from '@flip7/shared';
import { useAuth } from '../contexts/AuthContext';
import { ActiveGroupBanner } from '../components/ActiveGroupBanner';
import { OnlineFriends } from '../components/OnlineFriends';
import { BottomSheet } from '../components/BottomSheet';

interface HomeScreenProps {
  onCreateRoom: (playerName: string, targetScore: number, gameMode?: GameMode, botCount?: number, tier?: TableTier, variant?: GameVariant) => void;
  onJoinRoom: (roomCode: string, playerName: string) => void;
  onSpectateRoom: (roomCode: string, playerName: string) => void;
  onJoinGroup: (groupSlug: string, playerName: string) => void;
  onStoryMode: () => void;
  onDailyChallenge: () => void;
  onShop: () => void;
  onProfile: () => void;
  onCollection: () => void;
  onDuelPub: () => void;
  onFriends: () => void;
  error: string | null;
  initialRoomCode?: string;
  groupSlug?: string;
}

declare const __COMMIT_HASH__: string;

export function HomeScreen({ onCreateRoom, onJoinRoom, onSpectateRoom, onJoinGroup, onStoryMode, onDailyChallenge, onShop, onProfile, onCollection, onDuelPub, onFriends, error, initialRoomCode, groupSlug }: HomeScreenProps) {
  const { user, isLoading: authLoading, register, login, signOut, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name ?? localStorage.getItem('flip7_guest_name') ?? '');
  const [roomCode, setRoomCode] = useState((initialRoomCode ?? '').replace(/^F7-/i, ''));
  const [targetScore, setTargetScore] = useState(DEFAULT_TARGET_SCORE);
  const [gameMode, setGameMode] = useState<GameMode>('digital');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [botCount, setBotCount] = useState(0);
  const [tier, setTier] = useState<TableTier>('free');
  const [variant, setVariant] = useState<GameVariant>('standard');

  // Sheet state
  const [playSheet, setPlaySheet] = useState<'create' | 'join' | null>(
    initialRoomCode ? 'join' : null
  );
  const [groupSheet, setGroupSheet] = useState(!!groupSlug);
  const [groupInput, setGroupInput] = useState(groupSlug ?? '');
  const [authSheet, setAuthSheet] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [authName, setAuthName] = useState('');
  const [authPassphrase, setAuthPassphrase] = useState('');
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [authError, setAuthError] = useState('');

  const [dailyBonusClaimed, setDailyBonusClaimed] = useState(false);
  const [dailyBonusAmount, setDailyBonusAmount] = useState<number | null>(null);
  const canSubmit = name.trim().length >= 1 && name.trim().length <= 20;

  // Check if daily bonus was already claimed today on mount
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('flip7_access_token');
    if (!token) return;
    const apiBase = import.meta.env.VITE_API_URL || '';
    fetch(`${apiBase}/api/users/me/daily-bonus`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    }).then(res => res.json()).then(data => {
      if (data.awarded) {
        setDailyBonusAmount(data.amount);
        setDailyBonusClaimed(true);
        refreshUser();
      } else {
        setDailyBonusClaimed(true);
      }
    }).catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const claimDailyBonus = async () => {
    const token = localStorage.getItem('flip7_access_token');
    if (!token) return;
    try {
      const apiBase = import.meta.env.VITE_API_URL || '';
      const res = await fetch(`${apiBase}/api/users/me/daily-bonus`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.awarded) {
        setDailyBonusAmount(data.amount);
        setDailyBonusClaimed(true);
        refreshUser();
      } else {
        setDailyBonusClaimed(true);
      }
    } catch { /* ignore */ }
  };

  const handleQuickPlay = () => {
    const playerName = user?.name ?? name.trim();
    if (!playerName) return;
    onCreateRoom(playerName, 150, 'digital', 2, 'free', 'standard');
  };

  const handleCreate = () => {
    if (!canSubmit) return;
    if (!user) localStorage.setItem('flip7_guest_name', name.trim());
    onCreateRoom(name.trim(), targetScore, gameMode, gameMode === 'digital' ? botCount : undefined, gameMode === 'digital' ? tier : undefined, gameMode === 'digital' ? variant : undefined);
  };

  const handleJoin = () => {
    if (!canSubmit || !roomCode.trim()) return;
    if (!user) localStorage.setItem('flip7_guest_name', name.trim());
    const code = roomCode.trim().toUpperCase();
    onJoinRoom(code.startsWith('F7-') ? code : `F7-${code}`, name.trim());
  };

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authName.trim() || !authPassphrase) return;
    setAuthSubmitting(true);
    setAuthError('');
    try {
      if (authMode === 'register') {
        await register(authName.trim(), authPassphrase);
      } else {
        await login(authName.trim(), authPassphrase);
      }
      setName(authName.trim());
      setAuthSheet(false);
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setAuthSubmitting(false);
    }
  };

  return (
    <div className="hub">
      <div className="hero-panel">
        <div className="screen" style={{ gap: '0', paddingTop: '64px', paddingBottom: '40px', alignItems: 'center' }}>
          {/* Hero */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginBottom: 'var(--space-2xl)' }}>
            <h1 className="title-logo" style={{ fontSize: '5rem', marginBottom: '4px' }}>FLIP 7</h1>
            <div className="gold-divider" style={{ width: '72px', height: '3px' }} />
            <p className="subtitle" style={{ marginTop: '8px', fontSize: '0.75rem' }}>The Card Game</p>
          </div>

          {/* Shiny event banner */}
          {SHINY_EVENT && new Date() < SHINY_EVENT.endsAt && (
            <div className="shiny-event-banner" onClick={user ? onCollection : undefined} style={{ marginBottom: 'var(--space-md)' }}>
              <div className="shiny-event-banner__content">
                <span className="shiny-event-banner__icon">&#10024;</span>
                <div className="shiny-event-banner__text">
                  <div className="shiny-event-banner__title">{SHINY_EVENT.name}</div>
                  <div className="shiny-event-banner__detail">
                    Ends {SHINY_EVENT.endsAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </div>
                </div>
                <div className="shiny-event-banner__multiplier">{SHINY_EVENT.multiplier}x Shiny</div>
              </div>
            </div>
          )}

          {/* Coin balance + daily bonus */}
          {user && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              background: 'linear-gradient(135deg, rgba(255, 248, 225, 0.9), rgba(255, 224, 130, 0.6))',
              border: '1px solid var(--gold)', borderRadius: 'var(--radius-pill)',
              padding: '5px 14px', marginBottom: 'var(--space-lg)',
              boxShadow: 'var(--shadow-sm), var(--shadow-glow)',
            }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: '0.85rem', fontWeight: 800, color: 'var(--gold-dim)' }}>
                {user.coins ?? 0} coins
              </span>
              {!dailyBonusClaimed ? (
                <button onClick={claimDailyBonus} style={{
                  background: 'linear-gradient(180deg, var(--gold-light), var(--gold))',
                  border: 'none', borderRadius: 'var(--radius-pill)', padding: '3px 10px',
                  fontSize: '0.6rem', fontWeight: 700, color: 'white', cursor: 'pointer',
                  boxShadow: '0 1px 3px rgba(201, 152, 42, 0.3)', textShadow: '0 1px 1px rgba(0,0,0,0.15)',
                }}>+50 Bonus</button>
              ) : dailyBonusAmount ? (
                <span className="badge-pop" style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--gold-dim)' }}>
                  +{dailyBonusAmount}!
                </span>
              ) : null}
            </div>
          )}

          {/* Active groups + online friends */}
          <ActiveGroupBanner onJoinGroup={onJoinGroup} />
          <OnlineFriends
            onJoinRoom={(code) => onJoinRoom(code, user?.name ?? '')}
            onSpectateRoom={(code) => onSpectateRoom(code, user?.name ?? '')}
          />

          {/* Quick Play (large CTA) */}
          {user && (
            <button className="btn btn-primary" onClick={handleQuickPlay} style={{
              width: '100%', maxWidth: '340px', padding: '18px 24px',
              fontSize: '1.15rem', fontFamily: 'var(--font-display)',
              letterSpacing: '0.08em', marginBottom: 'var(--space-md)',
            }}>QUICK PLAY</button>
          )}

          {/* Game modes grid */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px',
            width: '100%', maxWidth: '340px', marginBottom: 'var(--space-xl)',
          }}>
            {[
              { key: 'create', icon: '\u271A', label: 'Create Room', color: 'var(--maroon)', onClick: () => setPlaySheet('create') },
              { key: 'join', icon: '\u279C', label: 'Join Room', color: 'var(--maroon)', onClick: () => setPlaySheet('join') },
              ...(user ? [
                { key: 'story', icon: '\u2726', label: 'Story Mode', color: 'var(--gold-dim)', onClick: onStoryMode, accent: 'var(--gold)' },
                { key: 'daily', icon: '\u2605', label: 'Daily', color: '#7B1FA2', onClick: onDailyChallenge, accent: '#7B1FA2' },
                { key: 'duel', icon: '\u2694\uFE0F', label: 'Duel', color: '#C62828', onClick: onDuelPub, accent: '#C62828' },
                { key: 'hotstreak', icon: '\uD83C\uDFC7', label: 'Hot Streak', color: '#2E7D32', onClick: () => {
                  const playerName = user?.name ?? name.trim();
                  if (!playerName) return;
                  onCreateRoom(playerName, 100, 'hot-streak');
                }, accent: '#2E7D32' },
              ] : []),
            ].map(item => (
              <button key={item.key} onClick={item.onClick} style={{
                cursor: 'pointer', padding: '20px 10px 18px', textAlign: 'center',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                background: 'linear-gradient(180deg, #FFFFFF, var(--parchment))',
                border: `1.5px solid ${'accent' in item && item.accent ? item.accent : 'var(--border-ornate)'}`,
                borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-sm)',
                transition: 'transform var(--transition-fast), box-shadow var(--transition-fast)',
              }}>
                <span style={{ fontSize: '1.6rem', lineHeight: 1, color: item.color }}>{item.icon}</span>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: '0.7rem', color: item.color, letterSpacing: '0.03em' }}>
                  {item.label}
                </span>
              </button>
            ))}
          </div>

          {/* Auth section */}
          {!authLoading && (
            <div style={{ textAlign: 'center' }}>
              {user ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.7rem' }}>
                  <span style={{ color: 'var(--text-muted)' }}>
                    {user.activeAvatar && <span style={{ marginRight: '3px' }}>{user.activeAvatar}</span>}
                    <strong style={{ color: 'var(--text-dark)' }}>{user.name}</strong>
                  </span>
                  <span style={{ color: 'var(--border-subtle)' }}>|</span>
                  <button onClick={onShop} style={{
                    background: 'none', border: 'none', color: 'var(--maroon)',
                    fontSize: '0.65rem', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontWeight: 600,
                  }}>Shop</button>
                  <span style={{ color: 'var(--border-subtle)' }}>|</span>
                  <button onClick={onCollection} style={{
                    background: 'none', border: 'none', color: 'var(--gold)',
                    fontSize: '0.65rem', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontWeight: 600,
                  }}>Shinies</button>
                  <span style={{ color: 'var(--border-subtle)' }}>|</span>
                  <button onClick={signOut} style={{
                    background: 'none', border: 'none', color: 'var(--text-faint)',
                    fontSize: '0.65rem', cursor: 'pointer', textDecoration: 'underline', padding: 0,
                  }}>Sign out</button>
                </div>
              ) : (
                <button onClick={() => setAuthSheet(true)} style={{
                  background: 'none', border: 'none', color: 'var(--maroon)',
                  fontSize: '0.7rem', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontWeight: 600,
                }}>Sign in to unlock all features</button>
              )}
            </div>
          )}

          <p style={{ fontSize: '0.5rem', color: 'var(--text-faint)', fontFamily: 'monospace', letterSpacing: '0.05em', marginTop: 'var(--space-xl)' }}>
            {__COMMIT_HASH__}
          </p>
        </div>
      </div>

      {/* ─── Play Sheet ─── */}
      <BottomSheet
        open={playSheet !== null}
        onClose={() => setPlaySheet(null)}
        title={playSheet === 'create' ? 'Create Game' : 'Join Game'}
      >
        <div className="play-form">
          {/* Name input */}
          {!user && (
            <div className="play-form__section">
              <label className="label">Your Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                maxLength={20}
                autoFocus
              />
            </div>
          )}

          {/* Create vs Join toggle */}
          <div className="play-form__row">
            <button
              className={`play-form__option${playSheet === 'create' ? ' play-form__option--active' : ''}`}
              onClick={() => setPlaySheet('create')}
            >
              <span className="play-form__option-label">Create</span>
              <span className="play-form__option-desc">New room</span>
            </button>
            <button
              className={`play-form__option${playSheet === 'join' ? ' play-form__option--active' : ''}`}
              onClick={() => setPlaySheet('join')}
            >
              <span className="play-form__option-label">Join</span>
              <span className="play-form__option-desc">Enter code</span>
            </button>
          </div>

          {playSheet === 'join' && (
            <div className="play-form__section">
              <label className="label">Room Code</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-muted)' }}>F7-</span>
                <input
                  type="text"
                  value={roomCode}
                  onChange={(e) => setRoomCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
                  placeholder="XXXXXX"
                  maxLength={6}
                  autoFocus
                  style={{ textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 800, fontFamily: 'var(--font-display)', fontSize: '1.1rem', textAlign: 'center', flex: 1 }}
                />
              </div>
            </div>
          )}

          {playSheet === 'create' && (
            <>
              {/* Game Mode */}
              <div className="play-form__section">
                <label className="label">Game Mode</label>
                <div className="play-form__row">
                  <button
                    className={`play-form__option${gameMode === 'digital' ? ' play-form__option--active' : ''}`}
                    onClick={() => setGameMode('digital')}
                  >
                    <span className="play-form__option-label">Digital</span>
                    <span className="play-form__option-desc">Cards dealt by server</span>
                  </button>
                  <button
                    className={`play-form__option${gameMode === 'score-tracker' ? ' play-form__option--active' : ''}`}
                    onClick={() => setGameMode('score-tracker')}
                  >
                    <span className="play-form__option-label">Tracker</span>
                    <span className="play-form__option-desc">Physical deck</span>
                  </button>
                </div>
              </div>

              {/* Advanced toggle */}
              <button
                className="play-form__advanced-toggle"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? '\u25BE Hide options' : '\u25B8 More options'}
              </button>

              {showAdvanced && (
                <>
                  <div className="play-form__section">
                    <label className="label">Target Score</label>
                    <input type="number" value={targetScore} onChange={(e) => setTargetScore(Number(e.target.value))} min={50} max={1000} step={50} />
                  </div>

                  {gameMode === 'digital' && (
                    <>
                      <div className="play-form__section">
                        <label className="label">Bot Players</label>
                        <div className="toggle-group">
                          {[0, 1, 2, 3].map(n => (
                            <button key={n} type="button" className={`toggle-group__item${botCount === n ? ' toggle-group__item--active' : ''}`} onClick={() => setBotCount(n)}>
                              {n === 0 ? 'None' : n}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="play-form__section">
                        <label className="label">Card Set</label>
                        <div className="toggle-group">
                          {([
                            { value: 'standard' as const, label: 'Standard' },
                            { value: 'vengeance' as const, label: 'Vengeance' },
                            { value: 'vengeance-brutal' as const, label: 'Brutal' },
                          ]).map(opt => (
                            <button key={opt.value} type="button" className={`toggle-group__item${variant === opt.value ? ' toggle-group__item--active' : ''}`} onClick={() => setVariant(opt.value)}>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {user && (
                        <div className="play-form__section">
                          <label className="label">Table Tier</label>
                          <div className="toggle-group">
                            {(['free', 'bronze', 'silver', 'gold'] as const).map(t => {
                              const cfg = TABLE_TIERS[t];
                              const canAfford = (user.coins ?? 0) >= cfg.entryFee;
                              return (
                                <button
                                  key={t}
                                  type="button"
                                  className={`toggle-group__item${tier === t ? ' toggle-group__item--active' : ''}`}
                                  onClick={() => canAfford && setTier(t)}
                                  disabled={!canAfford}
                                  style={{ textTransform: 'capitalize', opacity: canAfford ? 1 : 0.4 }}
                                >
                                  {t}
                                </button>
                              );
                            })}
                          </div>
                          <p className="play-form__hint">
                            {tier === 'free' ? `Winner earns ${TABLE_TIERS.free.winReward} coins` : `Entry: ${TABLE_TIERS[tier].entryFee} coins`}
                            {' \u00B7 '} Balance: {user.coins ?? 0}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}

          {error && <p className="error-text">{error}</p>}

          {/* Submit */}
          {playSheet === 'create' ? (
            <button className="btn btn-primary" onClick={handleCreate} disabled={!canSubmit} style={{ width: '100%' }}>
              Create Room
            </button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
              <button className="btn btn-primary" onClick={handleJoin} disabled={!canSubmit || !roomCode.trim()} style={{ width: '100%' }}>
                Join
              </button>
              <button className="btn btn-secondary" onClick={() => { if (canSubmit && roomCode.trim()) { const c = roomCode.trim().toUpperCase(); onSpectateRoom(c.startsWith('F7-') ? c : `F7-${c}`, name.trim()); } }} disabled={!canSubmit || !roomCode.trim()} style={{ width: '100%' }}>
                Watch
              </button>
            </div>
          )}
        </div>
      </BottomSheet>

      {/* ─── Group Sheet ─── */}
      <BottomSheet open={groupSheet} onClose={() => setGroupSheet(false)} title={groupSlug ? `Group: ${groupSlug}` : 'Join Group'}>
        <form
          className="play-form"
          onSubmit={(e) => { e.preventDefault(); const slug = groupSlug || groupInput.trim().toLowerCase(); if (canSubmit && slug) onJoinGroup(slug, name.trim()); }}
        >
          {!groupSlug && (
            <div className="play-form__section">
              <label className="label">Group Name</label>
              <input type="text" value={groupInput} onChange={(e) => setGroupInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} placeholder="e.g. friday-crew" maxLength={30} autoFocus />
            </div>
          )}
          {!user && (
            <div className="play-form__section">
              <label className="label">Your Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter your name" maxLength={20} />
            </div>
          )}
          {error && <p className="error-text">{error}</p>}
          <button type="submit" className="btn btn-primary" disabled={!canSubmit || (!groupSlug && !groupInput.trim())} style={{ width: '100%' }}>
            Join Group
          </button>
        </form>
      </BottomSheet>

      {/* ─── Auth Sheet ─── */}
      <BottomSheet open={authSheet} onClose={() => setAuthSheet(false)} title={authMode === 'login' ? 'Sign In' : 'Create Account'}>
        <form className="play-form" onSubmit={handleAuthSubmit}>
          <div className="toggle-group" style={{ marginBottom: '4px' }}>
            {(['login', 'register'] as const).map(m => (
              <button
                key={m}
                type="button"
                className={`toggle-group__item${authMode === m ? ' toggle-group__item--active' : ''}`}
                onClick={() => { setAuthMode(m); setAuthError(''); }}
              >
                {m === 'login' ? 'Sign In' : 'Register'}
              </button>
            ))}
          </div>

          <div className="play-form__section">
            <label className="label">Name</label>
            <input
              type="text" value={authName} onChange={(e) => setAuthName(e.target.value)}
              placeholder="Your player name" maxLength={20} autoFocus required
            />
          </div>
          <div className="play-form__section">
            <label className="label">Passphrase</label>
            <input
              type="password" value={authPassphrase} onChange={(e) => setAuthPassphrase(e.target.value)}
              placeholder={authMode === 'register' ? 'Choose a passphrase (4+ chars)' : 'Your passphrase'}
              minLength={4} required
            />
          </div>
          <p className="play-form__hint">
            {authMode === 'register'
              ? 'Pick a unique name and passphrase to track achievements.'
              : 'Sign in with your name and passphrase.'}
          </p>
          {authError && <p className="error-text">{authError}</p>}
          <button type="submit" className="btn btn-primary" disabled={authSubmitting || !authName.trim() || authPassphrase.length < 4} style={{ width: '100%' }}>
            {authSubmitting ? 'Loading...' : authMode === 'register' ? 'Create Account' : 'Sign In'}
          </button>
        </form>
      </BottomSheet>
    </div>
  );
}
